import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { cn } from "../../lib/utils";

interface LevelInfo {
  level: number;
  currentXp: number;
  nextLevelXp: number;
  progressPercent: number;
  badgeColor: string;
}

interface GamifiedAvatarProps {
  user: {
    id: string;
    name?: string;
    email: string;
    levelInfo?: LevelInfo;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  avatarUrl?: string;
}

export default function GamifiedAvatar({ 
  user, 
  size = 'md', 
  className,
  avatarUrl
}: GamifiedAvatarProps): JSX.Element {
  const levelInfo = user.levelInfo || {
    level: 1,
    currentXp: 0,
    nextLevelXp: 50,
    progressPercent: 0,
    badgeColor: '#94a3b8'
  };
  
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-20 w-20'
  };

  const ringSizes = {
    sm: { stroke: 2, radius: 15, center: 16 },
    md: { stroke: 3, radius: 22, center: 24 },
    lg: { stroke: 4, radius: 36, center: 40 }
  };

  const { stroke, radius } = ringSizes[size];
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (levelInfo.progressPercent / 100) * circumference;

  const getInitials = (name?: string, email?: string) => {
    const text = name || email || '??';
    const parts = text.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return text.slice(0, 2).toUpperCase();
  };

  return (
    <div className={cn("relative flex items-center justify-center shrink-0", className)}>
      {/* Progress Ring */}
      <svg className={cn("absolute -rotate-90 transform", size === 'sm' ? 'h-9 w-9' : size === 'md' ? 'h-14 w-14' : 'h-24 w-24')}>
        {/* Background Circle */}
        <circle
          cx={size === 'sm' ? 18 : size === 'md' ? 28 : 48}
          cy={size === 'sm' ? 18 : size === 'md' ? 28 : 48}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="transparent"
          className="text-muted-foreground"
        />
        {/* Progress Circle */}
        <circle
          cx={size === 'sm' ? 18 : size === 'md' ? 28 : 48}
          cy={size === 'sm' ? 18 : size === 'md' ? 28 : 48}
          r={radius}
          stroke={levelInfo.badgeColor}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          className="transition-all duration-1000 ease-out"
        />
      </svg>

      {/* Avatar */}
      <Avatar className={cn(sizeClasses[size], "ring-2 ring-border/50 shadow-inner transition-all")}>
        <AvatarImage src={avatarUrl} alt={user.name || user.email} />
        <AvatarFallback className="bg-background text-white font-black text-xs">
          {getInitials(user.name, user.email)}
        </AvatarFallback>
      </Avatar>
    </div>
  );
}
